import type { Style, EditingBehaviors } from '@wix/component-protocol/schema';
import { GetManifestValue } from '../manifestServices';
import { ManifestDefinitions } from '../types';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';

function getPresets({ manifest }: Pick<GetManifestValue, 'manifest'>) {
  const presetsEntries = Object.entries(manifest?.presets ?? {});

  if (!presetsEntries.length) {
    return null;
  }

  return presetsEntries.map(([key, value]) => ({
    id: key,
    name: value.displayName,
    src: value.imgUrl ?? '',
  }));
}

function getStyleDefinition({
  manifest,
}: Pick<GetManifestValue, 'manifest'>): Style | null {
  return manifest?.definitions?.style ?? null;
}

function getDisplayName({ manifest }: Pick<GetManifestValue, 'manifest'>) {
  return manifest?.displayName ?? null;
}

function getBehaviorsValue({ property, manifest }: GetManifestValue): boolean {
  return (
    manifest?.definitions?.editingBehaviors?.[
      property as keyof EditingBehaviors
    ] ?? false
  );
}

function getCompType({ manifest }: Pick<GetManifestValue, 'manifest'>): string {
  return manifest?.compType ?? '';
}

function getRootManifestData({ manifest, property }: GetManifestValue) {
  const handlers = {
    presets: getPresets,
    displayName: getDisplayName,
  };

  const handler = handlers[property as keyof typeof handlers];

  if (!handler) {
    throw createEditorPlatformManifestServicesError(
      EditorPlatformManifestServicesErrorCode.NoSuchHandlerToGetManifestData,
      property,
    );
  }

  return handler({ manifest });
}

const getManifestValueHandlers: {
  readonly [key in ManifestDefinitions]: (props: GetManifestValue) => unknown;
} = {
  [ManifestDefinitions.behaviors]: getBehaviorsValue,
  [ManifestDefinitions.style]: getStyleDefinition,
  [ManifestDefinitions.rootManifestData]: getRootManifestData,
  [ManifestDefinitions.compType]: getCompType,
};

export { getManifestValueHandlers };
